import { ActionBar, CommonPage, Content, InRow, LoadableContent, OpenablePanel, PageTitle, ResponsiveGap } from "common/Grid"
import { WithPagination } from "common/Pagination"
import { TableView } from "./TableView"
import { useGetAvailableProductsMutation, useImportProductsMutation } from "store/action/import-export-products";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FormField, PrimaryButton } from "common/Forms";
import { SelectInput } from "common/Forms/inputs";
import { toast } from 'react-toastify';
import ImportProductsDialog from "./import-products";
import { useGetSiteIdOptionsMutation } from "store/action/options-store";
import { captureComponentError } from "utils/sentry";

export const Page = () => {
  const [getAvailableProductsApi, {isLoading, isError}] = useGetAvailableProductsMutation();
  const [importProductApi, {isLoading: importProductLoading}] = useImportProductsMutation();
  const [getSiteIdOptionsApi] = useGetSiteIdOptionsMutation();
  const [siteIdOptions, setSiteIdOptions] = useState([]);

  const pagedProductsInfo = useSelector((state) => state?.importExportProducts?.data?.data);
  const [searchParams, setSearchParams] = useSearchParams();
  const [bulkProductTargetSelectorIds, setBulkProductTargetSelectorIds] = useState({productIds: [],});
  const [isImportProductDialogOpened, setIsImportProductDialogOpened] = useState(false)
  const [value, setValue] = useState("10000");
  const cancelHandler = () => setIsImportProductDialogOpened(false);

  const searchFields = useMemo(() =>
  ({
    siteid: { name: 'siteid', default: undefined, },
    page: { name: 'p', default: 1,},
    limit: { name: 'ps', default: 10,},
  }), []);

  const getSearchValue = useCallback((name, defaultValue, getter = (value) => value) => {
    const value = searchParams.get(name);
    if (value === null) {
      return defaultValue;
    }
    return getter(value);
  }, [searchParams])

  const [advancedSearchFormUserInput, setAdvancedSearchFormUserInput] = useState({
    siteid: getSearchValue(searchFields.siteid.name, searchFields.siteid.default),
  });

  const page = useRef(getSearchValue(searchFields.page.name, searchFields.page.default, x => parseInt(x) ?? searchFields.page.default));
  const limit = useRef(getSearchValue(searchFields.limit.name, searchFields.limit.default, x => parseInt(x) ?? searchFields.limit.default));

  const applySearch = useCallback(() =>
  {
    const updatedSearchParams = createSearchParams(searchParams);
    advancedSearchFormUserInput.siteid ? updatedSearchParams.set(searchFields.siteid.name, advancedSearchFormUserInput.siteid) : updatedSearchParams.delete(searchFields.siteid.name);
    updatedSearchParams.set(searchFields.page.name, `${page.current}`);
    updatedSearchParams.set(searchFields.limit.name, `${limit.current}`);
    setSearchParams(updatedSearchParams);
  }, [searchFields, searchParams, advancedSearchFormUserInput, setSearchParams]);

  const [sorting, setSorting] = useState({ sortBy: null, sortOrder: null });

  const onSortChange = useCallback((sortBy, sortOrder) => {
    setSorting(prev =>
      prev.sortBy === sortBy && prev.sortOrder === sortOrder
        ? { sortBy: null, sortOrder: null }
        : { sortBy, sortOrder }
    );
  }, []);

  const [isAdvancedSearch, setIsAdvancedSearch] = useState();
  const isForceApplySearch = useRef(false);

  const quickSearchHandler = (updater) =>
  {
    page.current = 1;
    setAdvancedSearchFormUserInput(updater);
  };

  const onPageHandler = (pageRequest) =>
  {
    page.current = pageRequest + 1;
    applySearch();
  };
  const onPageSizeHandler = (sizeRequest) =>
  {
    limit.current = sizeRequest;
    page.current = 1;
    applySearch();
  };

  const searchAsync = useCallback(async () =>
  {
    await getAvailableProductsApi(
    {
      siteId: getSearchValue(searchFields.siteid.name, searchFields.siteid.default),
      page: page.current,
      limit: limit.current,
      ...(sorting.sortBy !== null && { sortBy: sorting.sortBy, sortOrder: sorting.sortOrder }),
    },);
  }, [searchFields, getAvailableProductsApi, getSearchValue, sorting]);


  const importProductHandler = async () => {
    const formattedProductIds = bulkProductTargetSelectorIds?.productIds?.map(item => item.id);
    try {
      setIsImportProductDialogOpened(false)

      const response = await importProductApi({productIds: formattedProductIds, siteId: value});

      if (response?.data?.status === 'SUCCESS') {
        if (response?.data?.message !== '')
        {
          toast.success(response?.data?.message);
        }
        if (response?.data?.warning !== '')
        {
          toast.warning(response?.data?.warning);
        }
        searchAsync();
      } else {
        toast.error("Something went wrong! Please try again");
      }
    } catch (error) {
      captureComponentError(error, { component: "ImportExportProducts", action: "importProductHandler" });
      toast.error("Something went wrong! Please try again");
    }
  }

// --------------------Export json file functionality if needed ---------------------------
  // const downloadBase64File = (base64Data, fileName) => {
  //   const link = document.createElement("a");
  //   link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64Data}`;
  //   link.download = fileName;
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  // const onExportProductHandler = async (bulkProductTargetSelectorIds) => {
  //   const formattedProductIds = bulkProductTargetSelectorIds?.productIds?.map(item => item.id);
  //   try
  //   {
  //     await exportProductsApi({productIds: formattedProductIds}).then((data) =>
  //     {
  //       const base64File = data?.data?.base64File;
  //       const fileName = "exported_products.json";
  //       if(base64File) {
  //         downloadBase64File(base64File, fileName);
  //       }else  {
  //         toast.error("Failed to download the file. No data received.");
  //       }
  //     })
  //   }
  //   catch (e)
  //   {
  //     window.alert(e.message || "Something went wrong while exporting the product.");
  //   }
  // };

  useEffect(() =>
  {
    searchAsync();
  }, [searchAsync]);

  useEffect(() =>
  {
    if (!isAdvancedSearch || isForceApplySearch.current)
    {
      isForceApplySearch.current = false;
      applySearch();
    }
  }, [isAdvancedSearch, applySearch]);

  useEffect(() => {
    async function fetchSiteIdOptions() {
      const response = await getSiteIdOptionsApi();
      const options = response?.data?.data ?? [];
      setSiteIdOptions(options);
    }
    fetchSiteIdOptions();
  }, [getSiteIdOptionsApi]);
  return (
    <>
      <CommonPage>
        <Content>
          <PageTitle title="Produts" />
          <ResponsiveGap left={false} right={false}>
            <OpenablePanel isOpenedByDefault={false}
              openControlRenderer=
              {
                action =>
                  <ActionBar classNames={['justify-between flex-wrap gap-2']}>
                    <InRow>
                      <FormField
                        id="siteid"
                        inputFactory={(id, name, oninValidition) =>
                          <SelectInput disabled={false}
                            id={id} name={name}
                            placeholder="Select site id"
                            selectedKey={advancedSearchFormUserInput.siteid}
                            optionProvider={() => siteIdOptions}
                            keySelector={(x => `${x.value}`)}
                            labelSelector={(x => `${x.label}`)}
                            onValidation={oninValidition}
                            onChange={(_, n, selector) => quickSearchHandler(prev => ({...prev, siteid: n ? selector(n) : undefined}))} />
                        } />
                    </InRow>
                    <InRow gap={2}>
                      <PrimaryButton
                        classNames={['min-w-[150px]']}
                        action={() => setBulkProductTargetSelectorIds({productIds: []})}
                        disabled={!bulkProductTargetSelectorIds.productIds?.length}>
                          Deselect all
                      </PrimaryButton>
                      <PrimaryButton
                        classNames={['min-w-[150px]']}
                        disabled={!bulkProductTargetSelectorIds.productIds?.length}
                        action={() => setIsImportProductDialogOpened(true)}
                      >{`Import (${bulkProductTargetSelectorIds.productIds?.length})`}</PrimaryButton>
                    </InRow>
                  </ActionBar>
              }
              closeControlRenderer=
              {
                action => <></>
              }
              onClose={()=> {}}
              onOpen={() => setIsAdvancedSearch(false)}>
            </OpenablePanel>
          </ResponsiveGap>
          <LoadableContent isLoading={isLoading || importProductLoading}
                           isError={isError}>
            <WithPagination id='pagedData'
              currentPage={(pagedProductsInfo?.currentPage ?? 1) - 1}
              totalPages={pagedProductsInfo?.totalPages ?? 0}
              totalItems={pagedProductsInfo?.total ?? 0}
              pageSize={limit.current ?? 0}
              onPageChange={onPageHandler}
              onPageSizeChange={onPageSizeHandler}
              paginationWrapper={x => <ResponsiveGap>{x}</ResponsiveGap>}>
              <TableView list={pagedProductsInfo?.data ?? []}
                bulkProductTargetSelectorIds={bulkProductTargetSelectorIds}
                setBulkProductTargetSelectorIds={setBulkProductTargetSelectorIds}
                sorting={sorting}
                onSortChange={onSortChange} />
            </WithPagination>
          </LoadableContent>
        </Content>
      </CommonPage>
      <ImportProductsDialog
        setValue={setValue}
        value={value}
        siteIdOptions={siteIdOptions}
        importProductHandler={importProductHandler}
        isOpened={isImportProductDialogOpened}
        onCancel={cancelHandler} />
    </>
  )
}